import { useCallback } from 'react';
import type { MenuProps } from 'antd';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { tpoOptionTestId, tpoRowActionsBtnTestId } from '../../../../config/testIds';
import type { CalendarRow } from '../../../../types/calendar';
import { usePlanDetailsContext } from '../../../context/PlanDetailsContext';
import { useResetRuleSession } from '../../../../rules/store/useResetRuleSession';
import { useEditSession } from '../../../EditSession/EditSessionContext';
import { flattenMenuSections } from '../../../utils/menuSections';

/**
 * Row actions that remain available read-only, mapped to the label they take.
 * Anything absent here is a mutation and is disabled instead.
 */
const READ_ONLY_ACTION_LABEL_KEY: Record<string, 'viewEventAction' | 'viewPromoAction' | undefined> = {
    edit_event: 'viewEventAction',
    edit_promo: 'viewPromoAction',
};

export type KeepFlag = 'keep_dates' | 'keep_promo';

/**
 * Maps a tenant's row-action menu key to the API field it writes. A key missing
 * here skips the rules below and renders an enabled row that does nothing when
 * clicked, so renaming a menu key means updating this map with it.
 */
export const KEEP_FLAG_BY_MENU_KEY: Record<string, KeepFlag | undefined> = {
    keep_dates: 'keep_dates',
    keep_promo: 'keep_promo',
};

/**
 * The event-level keep action. Not part of `KEEP_FLAG_BY_MENU_KEY` because it
 * writes the event endpoint rather than the promo's keep flags, and applies to a
 * row whether or not that row carries a promo.
 */
export const KEEP_EVENT_MENU_KEY = 'keep_event';

interface UseCalendarRowActionsOptions {
    onRequestDeleteEvent?: (record: CalendarRow) => void;
    onRequestKeepFlag?: (record: CalendarRow, flag: KeepFlag) => void;
    onRequestKeepEvent?: (record: CalendarRow) => void;
}

export function useCalendarRowActions(options?: UseCalendarRowActionsOptions) {
    const { defaultRowActionItems, labels } = useTpoGenConfig();
    const { setActiveView, setEditEventId, setEditPromoId, setAutoAddPromo } =
        usePlanDetailsContext();
    const { canEdit } = useEditSession();
    const { resetEventSession } = useResetRuleSession();
    const editSessionLabels = labels.editSession;
    const keepFlagLabels = labels.keepFlags;

    const { onRequestDeleteEvent, onRequestKeepFlag, onRequestKeepEvent } = options ?? {};

    const handleEditEvent = useCallback(
        (record: CalendarRow) => {
            resetEventSession();
            setActiveView('event');
            setEditEventId(record.event_id);
        },
        [resetEventSession, setActiveView, setEditEventId],
    );

    const handleEditPromo = useCallback(
        (record: CalendarRow) => {
            resetEventSession();
            setActiveView('event');
            setEditEventId(record.event_id);
            setEditPromoId(record.promo_id);
        },
        [resetEventSession, setActiveView, setEditEventId, setEditPromoId],
    );

    const handleAddPromo = useCallback(
        (record: CalendarRow) => {
            resetEventSession();
            setActiveView('event');
            setEditEventId(record.event_id);
            setAutoAddPromo(true);
        },
        [resetEventSession, setActiveView, setEditEventId, setAutoAddPromo],
    );

    const getRowActionMenuItems = useCallback(
        (record: CalendarRow): MenuProps['items'] => {
            const actionsBtnId = tpoRowActionsBtnTestId('calendar', record.key);
            // The flags live on the promo, so a row without one cannot carry them.
            const hasPromo = !!record.promo_id;
            const isStartInPast = record.promo_start_date
                ? dayjs(record.promo_start_date).isBefore(dayjs(), 'day')
                : false;

            const sections = defaultRowActionItems.map((section) =>
                section.map((item) => {
                    // Opening an event or promo stays valid read-only, so those two
                    // are relabelled rather than disabled; the rest mutate and go.
                    const readOnlyLabel = canEdit ? undefined : READ_ONLY_ACTION_LABEL_KEY[item.key];
                    const keepFlag = KEEP_FLAG_BY_MENU_KEY[item.key];

                    let label: React.ReactNode = readOnlyLabel ? editSessionLabels[readOnlyLabel] : item.label;
                    let disabled = item.disabled || (!canEdit && !readOnlyLabel);

                    if (item.key === KEEP_EVENT_MENU_KEY) {
                        label = record.keep_event ? `${keepFlagLabels.removePrefix}${item.label}` : item.label;
                    }

                    if (keepFlag) {
                        const isSet = keepFlag === 'keep_dates' ? !!record.keep_dates : !!record.keep_promo;
                        // Mutually exclusive: whichever flag is set locks the other out.
                        const blockedByOther =
                            keepFlag === 'keep_dates' ? !!record.keep_promo : !!record.keep_dates;
                        // A promo already under way must not gain Keep Dates, but one
                        // that already has it can still give it up.
                        const blockedByStart = keepFlag === 'keep_dates' && isStartInPast && !isSet;
                        // Keeping the event already pins every promo under it, so the
                        // promo-level flags cannot be changed until it is released.
                        const blockedByKeepEvent = !!record.keep_event;

                        label = isSet ? `${keepFlagLabels.removePrefix}${item.label}` : item.label;
                        disabled = disabled || !hasPromo || blockedByOther || blockedByStart || blockedByKeepEvent;

                        // Every disabled state needs a reason: a greyed row with no
                        // explanation reads as a broken action rather than a rule.
                        const reason = blockedByKeepEvent
                            ? keepFlagLabels.keptForEventTooltip
                            : !hasPromo
                            ? keepFlagLabels.noPromoTooltip
                            : blockedByOther
                            ? keepFlagLabels.mutuallyExclusiveTooltip
                            : blockedByStart
                            ? keepFlagLabels.promoStartedTooltip
                            : '';
                        if (reason) {
                            // AntD v4 only makes nested anchors inert on a disabled
                            // row, so a span inside the label still receives hover.
                            label = (
                                <Tooltip title={reason} placement="left" destroyTooltipOnHide>
                                    <span>{label}</span>
                                </Tooltip>
                            );
                        }
                    }

                    return {
                        key: item.key,
                        label: item.danger ? <span className="text-[#FF4D4F]">{label}</span> : label,
                        disabled,
                        'data-testid': tpoOptionTestId(actionsBtnId, item.key),
                        'data-state-disabled': disabled ? 'true' : undefined,
                        onClick: () => {
                            if (item.key === 'edit_event') {
                                handleEditEvent(record);
                            }
                            if (item.key === 'add_promo') {
                                handleAddPromo(record);
                            }
                            if (item.key === 'edit_promo') {
                                handleEditPromo(record);
                            }
                            if (item.key === 'delete_event') {
                                onRequestDeleteEvent?.(record);
                            }
                            if (keepFlag) {
                                onRequestKeepFlag?.(record, keepFlag);
                            }
                            if (item.key === KEEP_EVENT_MENU_KEY) {
                                onRequestKeepEvent?.(record);
                            }
                        },
                    };
                }),
            );

            return flattenMenuSections(sections);
        },
        [
            defaultRowActionItems,
            canEdit,
            editSessionLabels,
            keepFlagLabels,
            handleEditEvent,
            handleAddPromo,
            handleEditPromo,
            onRequestDeleteEvent,
            onRequestKeepFlag,
            onRequestKeepEvent,
        ],
    );

    return { getRowActionMenuItems, handleEditEvent, handleEditPromo };
}
